import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { z } from "zod"
import { uniq } from "~/utils/tsUtils"
import { meanBy } from "lodash"
import { GearSix, CalendarBlank } from "@phosphor-icons/react"
import type {
  ColDef,
  CellClickedEvent,
  SelectionChangedEvent,
  IAggFuncParams,
} from "ag-grid-community"
import type { AgGridReact } from "ag-grid-react"
import { useMemo, useRef, useState, useCallback } from "react"

import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { WorkspacePage } from "~/components/design-system/pages"
import { Button } from "~/components/design-system/components"
import SearchField from "~/components/planning/SearchField"
import Commandbar from "~/components/planning/Commandbar"
import { VendorBulkSettingsDialog } from "~/components/vendor-management/VendorBulkSettingsDialog"
import { VendorBulkClosingPeriodDialog } from "~/components/vendor-management/VendorBulkClosingPeriodDialog"
import { createStringColumn, createCountColumn, createCustomColumn } from "~/utils/columnTemplates"
import { api } from "~/utils/api"
import { useVariants } from "~/hooks/useVariants"
import { formatDate } from "~/utils/datetime"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { AggregationSelector, AggregationMethod } from "~/components/reusable/AggregationSelector"
import type { ProcurementSettings } from "~/utils/vendorSchemas"
import { procurementSettingsSchema } from "~/utils/vendorSchemas"
import { usePageTracking } from "~/hooks/usePageTracking"

export const Route = createFileRoute("/_auth/procurement/vendor-management/_layout/vendors")({
  component: RouteComponent,
})

export enum VendorManagementColumn {
  Name = "name",
  Country = "country",
  Variants = "variants",
  Skus = "skus",
  LeadTime = "leadTime",
  SettingsUpdatedAt = "settingsUpdatedAt",
}

export type VendorRow = {
  vendorId: string
  name: string
  country: string | null
  variantCount: number
  skuCount: number
  avgLeadTime: number
  settingsUpdatedAt: Date | null
  rawProcurementSettings: ProcurementSettings | null
}

function RouteComponent() {
  usePageTracking("Procurement - Vendor Management - Vendors")
  const navigate = useNavigate()
  const { data: vendors, isLoading } = api.vendor.getActive.useQuery()
  const allVariants = useVariants()
  const gridRef = useRef<AgGridReact<VendorRow>>(null)
  const [selectedVendors, setSelectedVendors] = useState<VendorRow[]>([])
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [bulkClosingPeriodOpen, setBulkClosingPeriodOpen] = useState(false)
  const [aggregation, setAggregation] = useSafeLocalStorage<string>(
    "vendor-management-aggregation",
    AggregationMethod.Vendor,
    z.string(),
  )

  const vendorRows: VendorRow[] = useMemo(() => {
    if (!vendors) return []

    const variantsByVendor = new Map<string, typeof allVariants>()
    for (const v of allVariants) {
      for (const vendor of v.vendors ?? []) {
        if (!variantsByVendor.has(vendor.name)) variantsByVendor.set(vendor.name, [])
        variantsByVendor.get(vendor.name)!.push(v)
      }
    }

    return vendors.map((vendor) => {
      const vendorVariants = variantsByVendor.get(vendor.name) ?? []
      const skuIds = uniq(vendorVariants.flatMap((v) => v.skuIds ?? []))
      const avgLeadTime =
        vendorVariants.length > 0
          ? Math.round(meanBy(vendorVariants, (v) => v.settings?.totalLeadTimeDays ?? 30))
          : 30

      const rawProcurementSettings = vendor.procurementSettings
        ? (() => {
            const parsed = procurementSettingsSchema.safeParse(vendor.procurementSettings)
            return parsed.success ? parsed.data : null
          })()
        : null

      return {
        vendorId: vendor.vendorId,
        name: vendor.name,
        country: vendor.country ?? null,
        variantCount: vendorVariants.length,
        skuCount: skuIds.length,
        avgLeadTime,
        settingsUpdatedAt: (vendor.procurementSettings as { settingsUpdatedAt?: string } | null)
          ?.settingsUpdatedAt
          ? new Date(
              (vendor.procurementSettings as { settingsUpdatedAt?: string }).settingsUpdatedAt!,
            )
          : null,
        rawProcurementSettings,
      }
    })
  }, [vendors, allVariants])

  const handleCellClicked = useMemo(
    () => (params: CellClickedEvent<VendorRow>) => {
      if (!params.data) return
      void navigate({
        to: "/procurement/vendor-management/$vendorId",
        params: { vendorId: params.data.vendorId },
      })
    },
    [navigate],
  )

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent<VendorRow>) => {
    setSelectedVendors(event.api.getSelectedRows())
  }, [])

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api.deselectAll()
    setSelectedVendors([])
  }, [])

  const defaultColDef: ColDef<VendorRow> = useMemo(
    () => ({ flex: 1, suppressHeaderMenuButton: true, wrapHeaderText: true, minWidth: 100 }),
    [],
  )

  const isGroupedByCountry = aggregation === AggregationMethod.Country

  const autoGroupColumnDef: ColDef<VendorRow> = useMemo(
    () => ({
      headerName: isGroupedByCountry ? "Country" : "Vendor",
      field: "name",
      minWidth: 250,
      pinned: "left",
      lockPinned: true,
      flex: 1,
      cellStyle: (params) => (!params.node.group ? { cursor: "pointer", fontWeight: 500 } : null),
      onCellClicked: handleCellClicked,
      cellRendererParams: { suppressCount: false },
    }),
    [handleCellClicked, isGroupedByCountry],
  )

  const colDefs: ColDef<VendorRow>[] = useMemo(
    () => [
      {
        colId: "countryGroup",
        field: "country",
        rowGroup: isGroupedByCountry,
        hide: true,
        valueFormatter: (params) => params.value ?? "Unknown",
      },
      createStringColumn<VendorRow>({
        colId: VendorManagementColumn.Name,
        headerName: "Vendor",
        field: "name",
        minWidth: 250,
        pinned: "left",
        lockPinned: true,
        hide: isGroupedByCountry,
        cellStyle: { cursor: "pointer", fontWeight: 500 },
        onCellClicked: handleCellClicked,
      }),
      createCountColumn<VendorRow>({
        colId: VendorManagementColumn.Variants,
        headerName: "Variants",
        field: "variantCount",
        onCellClicked: handleCellClicked,
      }),
      createCountColumn<VendorRow>({
        colId: VendorManagementColumn.Skus,
        headerName: "SKUs",
        field: "skuCount",
        onCellClicked: handleCellClicked,
      }),
      createCustomColumn<VendorRow>({
        colId: VendorManagementColumn.LeadTime,
        headerName: "Avg Lead Time",
        minWidth: 130,
        valueGetter: (params) => params.data?.avgLeadTime,
        valueFormatter: (params) => (params.value != null ? `${Math.round(params.value)}d` : "—"),
        aggFunc: (params: IAggFuncParams) => {
          const values = (params.rowNode.allLeafChildren ?? [])
            .map((n) => (n.data as VendorRow | undefined)?.avgLeadTime)
            .filter((v): v is number => v != null)
          return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null
        },
        onCellClicked: handleCellClicked,
      }),
      createCustomColumn<VendorRow>({
        colId: VendorManagementColumn.SettingsUpdatedAt,
        headerName: "Last Updated",
        minWidth: 140,
        valueGetter: (params) => params.data?.settingsUpdatedAt ?? null,
        valueFormatter: (params) => (params.value ? formatDate(params.value) : "—"),
        aggFunc: (params: IAggFuncParams) => {
          const values = (params.rowNode.allLeafChildren ?? [])
            .map((n) => (n.data as VendorRow | undefined)?.settingsUpdatedAt)
            .filter((v): v is Date => v != null)
          return values.length > 0 ? values.reduce((max, v) => (v > max ? v : max)) : null
        },
        onCellClicked: handleCellClicked,
      }),
    ],
    [handleCellClicked, isGroupedByCountry],
  )

  if (isLoading) return <LoadingOverlay />

  return (
    <>
      <WorkspacePage
        toolbar={
          <div className="flex items-center gap-3">
            <SearchField
              initialSearchText=""
              placeholder="Search vendors..."
              onSearchTextChange={(searchText) =>
                gridRef.current?.api.setGridOption("quickFilterText", searchText)
              }
              debounceTime={150}
            />
            <span className="label-base">Group by</span>
            <AggregationSelector
              selectedMethod={aggregation}
              aggregationMethods={[AggregationMethod.Vendor, AggregationMethod.Country]}
              onSelect={setAggregation}
              prefix=""
            />
            <span className="paragraph-sm text-fg-subtle">{vendorRows.length} active vendors</span>
          </div>
        }
      >
        <DataTableBaseV2<VendorRow>
          id="vendor-management-list"
          gridRef={gridRef}
          rowData={vendorRows}
          rowIdField="vendorId"
          columnDefs={colDefs}
          defaultColumnDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          disableColumnSizingPersistence
          groupHideParentOfSingleChild={false}
          rowSelection={{
            mode: "multiRow",
            checkboxes: true,
            headerCheckbox: true,
            groupSelects: "descendants",
          }}
          onSelectionChanged={handleSelectionChanged}
        />
      </WorkspacePage>
      <Commandbar
        count={selectedVendors.length}
        selectedText="vendors selected"
        onClear={handleClearSelection}
      >
        <Button variant="neutral" size="sm" onClick={() => setBulkEditOpen(true)}>
          <GearSix className="mr-1.5" />
          Edit Settings
        </Button>
        <Button variant="neutral" size="sm" onClick={() => setBulkClosingPeriodOpen(true)}>
          <CalendarBlank className="mr-1.5" />
          Add Closing Period
        </Button>
      </Commandbar>
      {bulkEditOpen && (
        <VendorBulkSettingsDialog
          selectedVendors={selectedVendors}
          open={bulkEditOpen}
          onOpenChange={setBulkEditOpen}
          onSuccess={handleClearSelection}
        />
      )}
      {bulkClosingPeriodOpen && (
        <VendorBulkClosingPeriodDialog
          selectedVendors={selectedVendors}
          allVendors={vendorRows}
          open={bulkClosingPeriodOpen}
          onOpenChange={setBulkClosingPeriodOpen}
          onSuccess={handleClearSelection}
        />
      )}
    </>
  )
}
